import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  createDistrict,
  getDistrict,
  listRegions,
  listStates,
  listZones,
  updateDistrict,
  type RegionRow,
  type StateRow,
  type ZoneRow,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';

export function DistrictFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { districtId } = useParams<{ districtId: string }>();
  const editing = Boolean(districtId);

  const [districtCode, setDistrictCode] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [stateId, setStateId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [states, setStates] = useState<StateRow[]>([]);
  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listStates(token).then((r) => setStates(r.data)).catch((e) => setError((e as Error).message));
    listZones(token).then((r) => setZones(r.data)).catch((e) => setError((e as Error).message));
    listRegions(token).then((r) => setRegions(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!districtId) return;
    getDistrict(token, districtId)
      .then((d) => {
        setDistrictCode(d.districtCode);
        setDistrictName(d.districtName);
        setStateId(d.stateId ?? '');
        setZoneId(d.zoneId ?? '');
        setRegionId(d.regionId ?? '');
        setEffectiveFromDate(d.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, districtId]);

  async function submit(): Promise<void> {
    if (!districtName.trim()) { setError(t('org.district.form.nameReq')); return; }
    if (!stateId) { setError(t('org.district.form.stateReq')); return; }
    if (!editing && !districtCode.trim()) { setError(t('org.district.form.codeReq')); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && districtId) {
        await updateDistrict(token, districtId, {
          districtName: districtName.trim(),
          stateId,
          zoneId: zoneId || undefined,
          regionId: regionId || undefined,
        });
      } else {
        await createDistrict(token, {
          districtCode: districtCode.trim(),
          districtName: districtName.trim(),
          stateId,
          zoneId: zoneId || undefined,
          regionId: regionId || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/districts');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const stateOptions = states.map((s) => ({ value: s.stateId, label: `${s.stateCode} — ${s.stateName}` }));
  const zoneOptions = zones.map((z) => ({ value: z.zoneId, label: `${z.zoneCode} — ${z.zoneName}` }));
  const regionOptions = regions.map((r) => ({ value: r.regionId, label: `${r.regionCode} — ${r.regionName}` }));

  return (
    <FormShell label="district-form" title={editing ? t('org.district.form.editTitle') : t('org.district.form.newTitle')} error={error} onSubmit={submit}>
      <Field label={t('org.field.code')} name="districtCode" value={districtCode} onChange={setDistrictCode} required placeholder={t('org.district.form.codePh')} />
      <Field label={t('org.field.name')} name="districtName" value={districtName} onChange={setDistrictName} required />
      <SelectField label={t('org.field.state')} name="stateId" value={stateId} onChange={setStateId} options={stateOptions} required />
      <SelectField label={t('org.field.zone')} name="zoneId" value={zoneId} onChange={setZoneId} options={zoneOptions} placeholder={t('org.common.noneNone')} />
      <SelectField label={t('org.field.region')} name="regionId" value={regionId} onChange={setRegionId} options={regionOptions} placeholder={t('org.common.noneNone')} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.district.form.create')} cancelTo="/org/districts" />
    </FormShell>
  );
}
